import { FloatingIcon } from "@/components/redesign/FloatingIcon";
import { getAiModelBrand } from "@/constants/aiModelBrands";
import type { LpAiModelsBlockProps } from "@/puck/types";

/** Staggered float offsets (seconds) so a row of icons drifts out of sync.
 *  Negative values desync without delaying the first paint. */
const FLOAT_DELAYS = [0, -1.8, -3.4];

export function LpAiModelsBlock({
  eyebrow,
  titleLead,
  titleAccent,
  subtitle,
  footnoteLead,
  footnoteAccent,
  models,
}: Readonly<LpAiModelsBlockProps>) {
  return (
    <section className="marketing-ai-models-band relative w-full overflow-hidden px-4 py-24 md:py-28">
      <div className="relative mx-auto w-full max-w-3xl text-center">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-blue-200">
          {eyebrow}
        </p>
        <h2 className="mt-5 font-monument text-4xl font-bold leading-[1.08] tracking-tight text-white md:text-6xl">
          {titleLead} <span className="text-white/90">{titleAccent}</span>
        </h2>
        <p className="mx-auto mt-6 max-w-xl font-monument text-base leading-relaxed text-white/60 md:text-lg">
          {subtitle}
        </p>
      </div>

      <div className="relative mx-auto mt-12 grid w-full max-w-4xl grid-cols-1 gap-7 sm:grid-cols-3 md:mt-16">
        {models.map((model, index) => {
          const brand = getAiModelBrand(model.brandId);
          return (
            <div
              key={`${model.brandId}-${index}`}
              className="flex flex-col items-center px-4 pt-6 text-center"
            >
              <FloatingIcon
                src={brand.src}
                alt={brand.alt}
                width={brand.width}
                height={brand.height}
                glow={brand.glow}
                delay={FLOAT_DELAYS[index % FLOAT_DELAYS.length]}
                className="mb-6"
              />
              <h3 className="font-monument text-xl font-semibold text-white">
                {model.name}
              </h3>
              <p className="mt-1 font-mono text-xs uppercase tracking-[0.06em] text-white/50">
                {model.sublabel}
              </p>
            </div>
          );
        })}
      </div>

      <p className="relative mx-auto mt-10 max-w-xl text-center font-monument text-[15px] text-white/60">
        {footnoteLead}{" "}
        <b className="font-semibold text-white">{footnoteAccent}</b>
      </p>
    </section>
  );
}
